/**
 * Equivalencias token → operaciones (pivote bolsa mensual).
 *
 * Ratio de referencia (1M tokens):
 *   ≈ 300 conceptos o 75 APUs o 20 chats
 *
 * Bolsas llenas:
 *   Start 2M → 600 o 150 o 40
 *   Pro   5M → 1 500 o 375 o 100
 *   Teams 15M base (+5M/seat) → Pro × asientos (vía plan_equivalence del API)
 *   Vitruvio 50M → 15 000 o 3 750 o 1 000 (Pro ×10, 1 usuario)
 *
 * Recarga en tienda: mismo ratio (3M ≈ 900/225/60).
 */

export interface PlanEq {
  concepts: number;
  apus: number;
  chats: number;
  tokens_month: number;
  /** @deprecated compat lectura; preferir tokens_month */
  tokens_week?: number;
}

const KIT_RATIO = { concepts: 300, apus: 75, chats: 20, tokens: 1_000_000 };

/** Fallback local si el API aún no manda `plan_equivalence`. */
export function fallbackPlanEquivalence(plan: string, seats = 1): PlanEq {
  if (plan === 'pro') {
    return { concepts: 1_500, apus: 375, chats: 100, tokens_month: 5_000_000 };
  }
  if (plan === 'vitruvio') {
    return { concepts: 15_000, apus: 3_750, chats: 1_000, tokens_month: 50_000_000 };
  }
  if (plan === 'teams') {
    const n = Math.max(seats, 3);
    return { concepts: 1_500 * n, apus: 375 * n, chats: 100 * n, tokens_month: 5_000_000 * n };
  }
  return { concepts: 600, apus: 150, chats: 40, tokens_month: 2_000_000 };
}

/** Escala la equivalencia de bolsa llena al saldo restante del mes. */
export function approxFromRemaining(
  remaining: number,
  eq: PlanEq,
): { concepts: number; apus: number; chats: number } {
  const q = eq.tokens_month > 0 ? eq.tokens_month : 1;
  const f = Math.max(0, remaining) / q;
  return {
    concepts: Math.floor(f * eq.concepts),
    apus: Math.floor(f * eq.apus),
    chats: Math.floor(f * eq.chats),
  };
}

/** Copy de kit: "3M tokens ≈ 900 conceptos o 225 APUs o 60 chats". */
export function kitEquivalenceLine(kitTokens: number, fmt: (n: number) => string): string {
  const f = kitTokens / KIT_RATIO.tokens;
  const c = Math.round(f * KIT_RATIO.concepts);
  const a = Math.round(f * KIT_RATIO.apus);
  const h = Math.round(f * KIT_RATIO.chats);
  return `${fmt(kitTokens)} tokens ≈ ${fmt(c)} conceptos o ${fmt(a)} APUs o ${fmt(h)} chats`;
}

/**
 * Equivalencia de una recarga EN CRÉDITOS con los promedios medidos × factor:
 * "400 créditos ≈ 1,183 conceptos o 57 APUs o 88 chats (promedios)". Una u otra cosa
 * sobre el mismo saldo, no todas juntas. Devuelve '' si faltan los promedios.
 */
export function kitEquivalenceCreditos(
  creditos: number,
  promedios: Array<{ id: string; creditos: number }>,
  fmt: (n: number) => string,
): string {
  const por = (id: string): number => promedios.find((p) => p.id === id)?.creditos ?? 0;
  const c = por('ia_completa');
  const a = por('apu');
  const h = por('chat');
  if (!(c > 0 && a > 0 && h > 0)) return '';
  return (
    `≈ ${fmt(Math.floor(creditos / c))} conceptos con IA o ${fmt(Math.floor(creditos / a))} APUs ` +
    `o ${fmt(Math.floor(creditos / h))} chats (promedios)`
  );
}
